import { debounce, find, isEmpty } from "lodash";
import { ConfigProvider, Divider, Form, FormInstance, Spin } from "antd";
import { SelectValue } from "antd/es/select";
import React, { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_MAX_PAGINATION,
  NO_REASON_SELECTED_CANCEL_REASON_CODE,
  validateMessages,
} from "../../../../../constants/common";
import styles from "./index.module.scss";
import * as basePriceAPI from "../../../../../services/basePrice";
import * as saveOfferAPI from "../../../../../services/saveOffer";
import * as eComProductApi from "../../../../../services/eComProduct";
import * as promotionApi from "../../../../../services/promotion";
import { BasePriceModel } from "../../../../../types/model/price";
import {
  PromotionNamePrefix,
  SaveOfferContent,
  SaveOfferDetailModel,
  SaveOfferType,
} from "../../../../../types/model/saveOffer";
import { SubscriptionPromotion } from "../../../../../types/model/promotion";
import { SaveOfferExtendBillingDate } from "./SaveOfferExtendBillingDate";
import { SaveOfferTitleAndDescription } from "./SaveOfferTitleAndDescription";
import { SaveOfferApplicationRules } from "./SaveOfferApplicationRules";
import { SaveOfferPromotion } from "./SaveOfferPromotion";
import { SaveOfferSwitchPlan } from "./SaveOfferSwitchPlan";
import { SaveOfferTypeSelect } from "./SaveOfferTypeSelect";
import { SaveOfferHeadsUpModal } from "./SaveOfferHeadsUpModal";

export interface Option {
  label: string;
  value: number;
}

interface Props {
  form: FormInstance;
  contents: SaveOfferContent[];
  setContents: (contents: SaveOfferContent[]) => void;
  saveOfferDetail?: SaveOfferDetailModel;
}

export function SaveOfferForm({ form, saveOfferDetail, contents, setContents }: Props) {
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isLoadingPromoListByBasePrice, setIsLoadingPromoListByBasePrice] = useState<boolean>(false);
  const [basePrices, setBasePrices] = useState<BasePriceModel[]>([]);
  const [cancelReasons, setCancelReasons] = useState<Option[]>([]);
  const [plans, setPlans] = useState<any>([]);
  const [selectedPlan, setSelectedPlan] = useState<any>(saveOfferDetail?.switchToPlan);
  const [planList, setPlanList] = useState<Option[]>([]);
  const [promotionListByBasePrice, setPromotionListByBasePrice] = useState<SubscriptionPromotion[]>([]);
  const [promotionListByPlan, setPromotionListByPlan] = useState<SubscriptionPromotion[]>([]);
  const [hasFilledForm, setHasFilledForm] = useState(false);
  const [selectedPromotionByBasePrice, setSelectedPromotionByBasePrice] = useState<SubscriptionPromotion>(
    {} as SubscriptionPromotion
  );
  const [selectedPromotionByPlan, setSelectedPromotionByPlan] = useState<SubscriptionPromotion>(
    {} as SubscriptionPromotion
  );
  const [headsUpModalVisible, setHeadsUpModalVisible] = useState(false);
  const [selectedSaveOfferType, setSelectedSaveOfferType] = useState<SaveOfferType>(
    saveOfferDetail?.type || SaveOfferType.PROMOTION
  );
  const isPromotionTypeSaveOffer = useMemo(() => selectedSaveOfferType === SaveOfferType.PROMOTION, [
    selectedSaveOfferType,
  ]);
  const isSwitchPlanTypeSaveOffer = useMemo(() => selectedSaveOfferType === SaveOfferType.SWITCH_PLAN, [
    selectedSaveOfferType,
  ]);

  useEffect(() => {
    setIsLoadingData(true);
    Promise.all([
      basePriceAPI.fetchBasePriceList({
        page: 1,
        limit: DEFAULT_MAX_PAGINATION.size,
      }),
      saveOfferAPI.fetchCancelReason(),
    ])
      .then(([basePriceList, cancelReasonData]) => {
        setBasePrices(basePriceList.list);
        setCancelReasons(
          cancelReasonData.map(({ code, description }) => ({
            label: description.en,
            value: code ?? NO_REASON_SELECTED_CANCEL_REASON_CODE,
          }))
        );
        setIsLoadingData(false);
      })
      .catch(() => {
        setIsLoadingData(false);
      });
  }, []);

  useEffect(() => {
    if (!saveOfferDetail || hasFilledForm) {
      return;
    }

    form.setFieldsValue({
      appliedStages: saveOfferDetail.appliedStages,
      cancelReasonCode: saveOfferDetail.cancelReason?.code,
      appliedBasePriceIds: saveOfferDetail.appliedBasePrices.map((x) => x.id),
      saveOfferType: saveOfferDetail.type,
      extendDays: saveOfferDetail.extendDays,
    });

    saveOfferDetail.contents.forEach((item) => {
      form.setFieldsValue({
        [`${item.locale}Title`]: item.title,
        [`${item.locale}Description`]: item.description,
      });
    });

    handleFetchPromotionsByBasePrice();
  }, [saveOfferDetail, hasFilledForm]);

  useEffect(() => {
    if (!saveOfferDetail || !saveOfferDetail.switchToPlan || hasFilledForm) {
      return;
    }

    handleVariantsSearch(saveOfferDetail.switchToPlan.pid).then();
  }, [basePrices, hasFilledForm]);

  useEffect(() => {
    if (!saveOfferDetail || !saveOfferDetail.switchToPlan || plans?.length <= 0 || hasFilledForm) {
      return;
    }

    form.setFieldsValue({
      switchToPlanPid: saveOfferDetail.switchToPlan.pid,
      effectiveDate: saveOfferDetail.effectiveDate,
    });
    handleSwitchPlanInputChange(saveOfferDetail.switchToPlan.pid);
  }, [plans, hasFilledForm]);

  useEffect(() => {
    if (!saveOfferDetail || !saveOfferDetail.promotion || hasFilledForm) {
      return;
    }

    const namePrefix = isPromotionTypeSaveOffer ? PromotionNamePrefix.BasePrice : PromotionNamePrefix.Plan;

    form.setFieldsValue({
      [`${namePrefix}PromotionCode`]: saveOfferDetail.promotion?.code,
    });
    const promotions = isPromotionTypeSaveOffer ? promotionListByBasePrice : selectedPlan?.recurlyPromotions;
    const setPromotion = isPromotionTypeSaveOffer ? setSelectedPromotionByBasePrice : setSelectedPromotionByPlan;
    if (promotions) {
      const selectedPromotion = find(promotions, {
        code: saveOfferDetail.promotion?.code,
      });
      if (selectedPromotion) setPromotion(selectedPromotion);
    }

    const promotionListLoaded =
      (isPromotionTypeSaveOffer && promotionListByBasePrice.length) ||
      (isSwitchPlanTypeSaveOffer && promotionListByPlan.length);

    if (promotionListLoaded) {
      setHasFilledForm(true);
    }
  }, [promotionListByBasePrice, promotionListByPlan, hasFilledForm, form]);

  const handleVariantsSearch = async (search: string): Promise<Option[]> => {
    const res = await eComProductApi.fetchProductVariantsByFuzzySearch(search);
    if (res) {
      const options = res.map(({ id, title, pid, billingPlan }) => ({
        key: id,
        label: billingPlan ? `${pid} ${title} / ${billingPlan.name}` : `${pid} ${title}`,
        value: pid,
      }));
      setPlanList(options);
      setPlans(res);
      return options;
    } else {
      return [];
    }
  };

  const handleFetchPromotionsByBasePrice = debounce(() => {
    const basePriceIds = form.getFieldValue("appliedBasePriceIds");
    if (!isEmpty(basePriceIds)) {
      setIsLoadingPromoListByBasePrice(true);
      promotionApi
        .fetchPromotionsByBasePriceIds(basePriceIds)
        .then((promotions) => {
          setPromotionListByBasePrice(promotions);
        })
        .finally(() => {
          setIsLoadingPromoListByBasePrice(false);
        });
    }
  }, 800);

  const handleHeadsUp = (selectedVariant: any) => {
    if (isSwitchPlanTypeSaveOffer) {
      const appliedBasePriceIds = form.getFieldValue("appliedBasePriceIds");
      const selectedPlanBasePrice = selectedVariant?.basePriceId;
      if (appliedBasePriceIds?.includes(selectedPlanBasePrice)) {
        setHeadsUpModalVisible(true);
      }
    }
  };

  const handleAppliedBasePriceIdsChange = () => {
    form.resetFields(["basePricePromotionCode"]);
    setSelectedPromotionByBasePrice({} as SubscriptionPromotion);
    setPromotionListByBasePrice([]);
    handleFetchPromotionsByBasePrice();
    handleHeadsUp(selectedPlan);
  };

  const handleSwitchPlanInputChange = (value: SelectValue) => {
    form.resetFields(["planPromotionCode"]);
    setSelectedPromotionByPlan({} as SubscriptionPromotion);
    const selectedVariant = plans?.find((plan: any) => plan.pid === value) ?? null;
    if (selectedVariant) {
      setSelectedPlan(selectedVariant);
      setPromotionListByPlan(selectedVariant.recurlyPromotions);
      handleHeadsUp(selectedVariant);
    } else {
      setSelectedPlan({});
      setPromotionListByPlan([]);
    }
  };

  return (
    <Spin spinning={isLoadingData} size="large">
      <ConfigProvider form={{ validateMessages }}>
        <Form form={form} validateTrigger={"onBlur"} layout="vertical" className={styles.saveOfferForm}>
          <SaveOfferApplicationRules
            basePriceList={basePrices}
            cancelReasonList={cancelReasons}
            onAppliedBasePriceIdsChange={handleAppliedBasePriceIdsChange}
          />

          <Divider className={styles.divider} />

          <SaveOfferTitleAndDescription contents={contents} setContents={setContents} />

          <Divider />

          <SaveOfferTypeSelect setSelectedSaveOfferType={setSelectedSaveOfferType} />

          <Form.Item noStyle shouldUpdate>
            {selectedSaveOfferType === SaveOfferType.SWITCH_PLAN && (
              <>
                <SaveOfferSwitchPlan
                  planList={planList}
                  setPlanList={setPlanList}
                  handleVariantsSearch={handleVariantsSearch}
                  handleSwitchPlanInputChange={handleSwitchPlanInputChange}
                />
                <SaveOfferPromotion
                  form={form}
                  selectedSaveOfferType={selectedSaveOfferType}
                  isPromotionListLoading={false}
                  promotionList={promotionListByPlan}
                  selectedPromotion={selectedPromotionByPlan}
                  setSelectedPromotion={setSelectedPromotionByPlan}
                />
              </>
            )}
            {selectedSaveOfferType === SaveOfferType.PROMOTION && (
              <SaveOfferPromotion
                form={form}
                selectedSaveOfferType={selectedSaveOfferType}
                isPromotionListLoading={isLoadingPromoListByBasePrice}
                promotionList={promotionListByBasePrice}
                selectedPromotion={selectedPromotionByBasePrice}
                setSelectedPromotion={setSelectedPromotionByBasePrice}
              />
            )}
            {selectedSaveOfferType === SaveOfferType.EXTEND_BILLING_DATE && <SaveOfferExtendBillingDate form={form} />}
          </Form.Item>
        </Form>
      </ConfigProvider>

      <SaveOfferHeadsUpModal
        headsUpModalVisible={headsUpModalVisible}
        setHeadsUpModalVisible={setHeadsUpModalVisible}
      />
    </Spin>
  );
}
